/**
 * Pure derivations for the Investor Lens gating + pre-screen UX on
 * RecommendedInvestorCard. Extracted so the rules can be unit-tested without
 * pulling in the full component (TanStack Router, context, query client).
 */

import type { PitchDeckStatus } from '../../../contexts/startup-guard'
import { EVALUATION_REQUEST_KIND, type EvaluationRequestKind } from '../../../lib/matches'

export interface LensGateInput {
  hasCompletedPitchSession: boolean
  pitchDeckStatus: PitchDeckStatus
  /** Server-driven kind on the existing eval request, if any. */
  evaluationRequestKind?: EvaluationRequestKind | null
}

export interface LensGateState {
  /** Founder is allowed to trigger an evaluation now. */
  canEvaluate: boolean
  /** Founder can evaluate but only via pre-screen (no completed pitch yet). */
  isPrescreenOnly: boolean
  /** Server says the existing/running/completed request is a pre-screen. */
  isPrescreenRequest: boolean
  /** Render a disabled "Deck still processing…" pill instead of CTAs. */
  isDeckProcessing: boolean
  /** True when there's no path forward — show "Record a pitch first". */
  needsPitchSession: boolean
}

export function deriveLensGate(input: LensGateInput): LensGateState {
  const hasReadyDeck = input.pitchDeckStatus === 'READY'
  const isDeckProcessing = input.pitchDeckStatus === 'PROCESSING'
  const canEvaluate = input.hasCompletedPitchSession || hasReadyDeck
  const isPrescreenOnly = !input.hasCompletedPitchSession && hasReadyDeck
  const isPrescreenRequest = input.evaluationRequestKind === EVALUATION_REQUEST_KIND.PRESCREEN
  const needsPitchSession = !input.hasCompletedPitchSession && !hasReadyDeck && !isDeckProcessing
  return {
    canEvaluate,
    isPrescreenOnly,
    isPrescreenRequest,
    isDeckProcessing: isDeckProcessing && !input.hasCompletedPitchSession,
    needsPitchSession,
  }
}
